import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProduct, addToCart, EshopProductVariant } from '../services/eshopApi';
import { Box, Heading, Text, Image, Badge, HStack, VStack, Button, Select, useToast } from '@chakra-ui/react';

const formatPrice = (cents: number, currency: string) => {
  const value = cents / 100;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);
};

const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const toast = useToast();
  const { data, isLoading, isError } = useQuery(['eshop-product', slug], () => getProduct(slug || ''), {
    enabled: !!slug,
  });
  const [variantId, setVariantId] = useState<number | undefined>(undefined);

  if (isLoading) return <Text>Načítání produktu…</Text>;
  if (isError || !data) return <Text>Produkt nebyl nalezen.</Text>;

  const variants = data.variants || [];

  const handleAddToCart = async () => {
    try {
      const selected = variants.find((v) => v.id === variantId) as EshopProductVariant | undefined;
      await addToCart(data.id, selected?.id, 1);
      toast({ status: 'success', title: 'Přidáno do košíku', description: data.name, duration: 2000 });
    } catch (e) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se přidat do košíku.' });
    }
  };

  return (
    <Box>
      <HStack align="flex-start" spacing={8} flexWrap="wrap">
        <Box flex="0 0 320px" maxW="100%">
          <Image
            src={data.default_image_url || '/images/placeholder-clothing.jpg'}
            alt={data.name}
            w="100%"
            borderRadius="md"
            mb={4}
          />
        </Box>
        <VStack align="stretch" spacing={4} flex="1">
          <Heading size="lg">{data.name}</Heading>
          <Badge colorScheme="blue" fontSize="md" alignSelf="flex-start">
            {formatPrice(data.price_cents, data.currency || 'CZK')}
          </Badge>
          {data.short_description && (
            <Text color="gray.700">{data.short_description}</Text>
          )}
          {variants.length > 0 && (
            <Box>
              <Text fontWeight="medium" mb={1}>Varianta</Text>
              <Select
                placeholder="Vyberte variantu"
                value={variantId ?? ''}
                onChange={(e) => setVariantId(e.target.value ? Number(e.target.value) : undefined)}
                maxW="260px"
              >
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name || v.sku || `Varianta #${v.id}`}
                  </option>
                ))}
              </Select>
            </Box>
          )}
          <Button colorScheme="blue" onClick={handleAddToCart} maxW="260px">
            Přidat do košíku
          </Button>
          {data.description_html && (
            <Box mt={4} fontSize="sm" color="gray.700">
              <div dangerouslySetInnerHTML={{ __html: data.description_html }} />
            </Box>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default ProductDetailPage;
